import { CanvasTextMetrics, Container, Graphics, Text, TextStyle } from 'pixi.js'
import type { RenderableBuilding } from '../hit-test'
import { colourFor, colours } from '../colours'
import {
  abbreviateShort,
  pickTextColour,
  shouldRotate,
  tierFor,
  truncateToFit,
  type LabelTier,
} from '../label'

const FONT_FAMILY = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
const FONT_SIZE = 12
const PADDING = 4

interface LabelData {
  fullStr: string
  shortStr: string
  rotated: boolean
  smallestCells: number
}

/**
 * Cache key fragments. The cache holds the binary-search result (which is
 * the expensive bit); rebuilds are triggered when any of these change for
 * a building.
 */
interface LabelKey {
  cityentityId: string
  width: number
  height: number
  cellSize: number
  /** Original (untruncated) name — when the catalogue resolves an unknown,
   *  the displayed source string changes shape and we want to recompute. */
  name: string
}

interface CacheEntry {
  key: LabelKey
  data: LabelData
}

interface CurrentLabel {
  text: Text
  data: LabelData
}

export class BuildingLayer {
  readonly container: Container = new Container()

  /** Per-building cache, keyed by building.id. The truncated strings only
   *  depend on cellSize, footprint, and source name — not on theme — so a
   *  theme switch keeps the cache valid (the colour is recomputed on draw,
   *  which is cheap). */
  private cache: Map<number, CacheEntry> = new Map()

  /** Live text refs from the most recent draw, used by applyZoomTier()
   *  to swap full/short/hidden without rebuilding the scene graph. */
  private currentLabels: Map<number, CurrentLabel> = new Map()

  private styleCache: TextStyle | null = null

  constructor(private cellSize: number) {
    this.container.label = 'building-layer'
  }

  setCellSize(cellSize: number): void {
    this.cellSize = cellSize
  }

  draw(buildings: RenderableBuilding[], worldScale: number = 1): void {
    this.container.removeChildren()
    this.currentLabels.clear()
    const c = this.cellSize
    const borderColour = colours.buildingBorder()
    const unknownFill = colours.unknownHatch()

    for (const b of buildings) {
      const px = b.building.x * c
      const py = b.building.y * c
      const w = b.width * c
      const h = b.height * c

      const isUnknown = !b.resolved
      const fill = isUnknown ? unknownFill : colourFor(b.resolved!.type)

      const g = new Graphics()
      // 0.7 fill alpha lets the grid lines show through every building so
      // the player can still read the underlying tile boundaries while
      // skimming the layout. Unknowns drop a touch further (0.5) so they
      // feel "less solid" than resolved entries.
      g.rect(px, py, w, h).fill({ color: fill, alpha: isUnknown ? 0.5 : 0.7 })
      g.rect(px, py, w, h).stroke({ width: 1, color: borderColour, alpha: 0.8 })

      if (isUnknown) {
        // Diagonal hatch pattern over unknowns. Cheap approximation, draws
        // /-direction lines at fixed pitch.
        const pitch = Math.max(6, c / 2)
        const hatch = new Graphics()
        for (let d = -h; d < w; d += pitch) {
          hatch.moveTo(px + d, py + h).lineTo(px + d + h, py)
        }
        hatch.stroke({ width: 1, color: 0x000000, alpha: 0.35 })
        const mask = new Graphics().rect(px, py, w, h).fill({ color: 0xffffff })
        hatch.mask = mask
        g.addChild(mask)
        g.addChild(hatch)
      }

      this.container.addChild(g)

      // Label work — both string forms and visibility tier.
      const sourceName = isUnknown
        ? `? ${b.building.cityentityId}`
        : b.resolved!.name
      const data = this.getOrComputeLabel(b, sourceName)

      // Skip Text creation only when there's literally nothing renderable
      // (empty source name; truncation produced no characters at all). For
      // hidden-by-tier we still create the Text so applyZoomTier() can flip
      // it visible when the user zooms in — otherwise a building that was
      // hidden at draw time never gets a label even after the zoom upgrade.
      if (data.fullStr === '' && data.shortStr === '') continue

      const tier = tierFor(data.smallestCells * c * worldScale)
      const labelText = pickLabelText(tier, data)

      const text = new Text({
        // Pixi 8's Text rejects an empty initial string. pickLabelText
        // resolves to a non-empty form whenever data has one (so a
        // hidden-tier seed still gets a real string we can flip back
        // to visible later); we only end up with '' when both fullStr
        // and shortStr are empty (empty source name).
        text: labelText || ' ',
        style: this.getStyle(pickTextColour(fill)),
        resolution: typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1,
      })
      text.anchor.set(0.5)
      text.position.set(px + w / 2, py + h / 2)
      if (data.rotated) text.rotation = -Math.PI / 2
      // Hide at the 'hidden' tier; show otherwise. pickLabelText returns
      // '' for the hidden tier, so the second clause is the real guard
      // (catches the rare case where both fullStr and shortStr are empty).
      text.visible = tier !== 'hidden' && labelText !== ''

      this.container.addChild(text)
      this.currentLabels.set(b.building.id, { text, data })
    }

    this.pruneCache(buildings)
  }

  /**
   * Re-evaluate label tier for every currently-rendered building given a
   * new world scale. Swaps text content (full / short) without re-measuring
   * or rebuilding Pixi.Text objects; hides the text when the building drops
   * below the lower threshold. Called by the scene after every zoom change.
   */
  applyZoomTier(worldScale: number): void {
    const c = this.cellSize
    for (const { text, data } of this.currentLabels.values()) {
      const tier = tierFor(data.smallestCells * c * worldScale)
      if (tier === 'hidden') {
        text.visible = false
        continue
      }
      const next = pickLabelText(tier, data)
      if (next === '') {
        text.visible = false
        continue
      }
      text.visible = true
      if (text.text !== next) text.text = next
    }
  }

  /** Drop cached entries when the theme or font changes — strictly the colour
   *  is the only thing that's invalidated, but redrawing is the cheapest way
   *  to make sure stale text styles don't linger. Called by scene.applyTheme(). */
  invalidateCache(): void {
    this.cache.clear()
    this.styleCache = null
  }

  destroy(): void {
    this.container.destroy({ children: true })
    this.cache.clear()
    this.currentLabels.clear()
  }

  // — internals —

  private getOrComputeLabel(b: RenderableBuilding, sourceName: string): LabelData {
    const id = b.building.id
    const cached = this.cache.get(id)
    const key: LabelKey = {
      cityentityId: b.building.cityentityId,
      width: b.width,
      height: b.height,
      cellSize: this.cellSize,
      name: sourceName,
    }
    if (cached && sameKey(cached.key, key)) return cached.data

    const rotated = shouldRotate(b.width, b.height)
    // Available world-pixel width along the long axis of the text. Vertical
    // text reads along the building's height; horizontal along its width.
    const availPx = (rotated ? b.height : b.width) * this.cellSize - 2 * PADDING
    const measure = this.measurer()
    const fullStr = truncateToFit(sourceName, measure, Math.max(0, availPx))
    const shortStr = abbreviateShort(sourceName)
    const data: LabelData = {
      fullStr,
      shortStr,
      rotated,
      smallestCells: Math.min(b.width, b.height),
    }
    this.cache.set(id, { key, data })
    return data
  }

  private pruneCache(buildings: RenderableBuilding[]): void {
    const present = new Set(buildings.map((b) => b.building.id))
    for (const id of this.cache.keys()) {
      if (!present.has(id)) this.cache.delete(id)
    }
  }

  private getStyle(textColour: number): TextStyle {
    // Pixi caches text-rendering metrics on the style object; one shared
    // style across labels of the same colour avoids the per-text style cost.
    // Different colours need different style instances. For the v1 draw we
    // create a fresh style per building — colour varies. The cost is small
    // compared to the rectangle / text creation already in the loop.
    return new TextStyle({
      fontFamily: FONT_FAMILY,
      fontSize: FONT_SIZE,
      fill: textColour,
      align: 'center',
    })
  }

  private measurer(): (s: string) => number {
    // Build the style once per draw pass (font is fixed, colour irrelevant
    // for measurement). Reused across binary-search iterations and across
    // buildings within the same draw.
    if (!this.styleCache) {
      this.styleCache = new TextStyle({
        fontFamily: FONT_FAMILY,
        fontSize: FONT_SIZE,
        fill: 0xffffff,
      })
    }
    const style = this.styleCache
    return (s: string) => CanvasTextMetrics.measureText(s, style).width
  }
}

/**
 * Tier-appropriate text with a graceful degrade chain. The 'full' tier
 * usually wants `fullStr`, but for very small footprints (1×1 buildings)
 * the fontSize-12 truncation can return '' when even one char + ellipsis
 * doesn't fit in the available world width. In that case we fall back to
 * `shortStr` rather than hiding the label — the player still sees
 * something meaningful when zooming in past the 28px threshold.
 */
function pickLabelText(tier: LabelTier, data: LabelData): string {
  if (tier === 'hidden') return ''
  if (tier === 'full' && data.fullStr) return data.fullStr
  if (data.shortStr) return data.shortStr
  return data.fullStr
}

function sameKey(a: LabelKey, b: LabelKey): boolean {
  return (
    a.cityentityId === b.cityentityId &&
    a.width === b.width &&
    a.height === b.height &&
    a.cellSize === b.cellSize &&
    a.name === b.name
  )
}
